/* eslint-disable no-restricted-imports */
import { ROOT_DOMAIN } from 'common/environment';
import { isMarkable } from 'common/instrumentation';
import { AMEX, DISCOVER, MASTERCARD, ONBOARDED_NETWORKS, VISA } from 'common/network-partners';
import { VISA_SDK_URI } from 'common/sdk-load-params';
import { saveSdkLoadInfo } from 'orch-communicator/store';
import { getSupportedCardNetworks, getSrcNetworkInfo } from 'orch-communicator/store/selectors';
import isValidRequestedNetwork from 'orch-communicator/utils/is-valid-network';
import { SRCInfo } from 'types/data';

// START GENAI@CHATGPT4
declare global {
  interface Window {
    VISA_SDK: object;
    //eslint-disable-next-line
    SRCSDK_MASTERCARD?: Function;
    SRCSDK_DISCOVER?: object;
    SRCSDK_AMEX?: object;
  }
}

interface SdkLoadInfo {
  loadedSdk?: object;
}

function initializeSDK(brand: string) {
  let SDK;

  switch (brand) {
    case VISA:
      SDK = window.VISA_SDK;
      break;
    case MASTERCARD:
      if (window.SRCSDK_MASTERCARD) {
        //eslint-disable-next-line
        SDK = window.SRCSDK_MASTERCARD();
      }

      break;
    case DISCOVER:
      SDK = window.SRCSDK_DISCOVER;
      break;
    case AMEX:
      SDK = window.SRCSDK_AMEX;
      break;
  }

  if (!SDK) {
    throw new Error(`!!! Failed to load ${brand} SDK`);
  }

  //eslint-disable-next-line
  saveSdkLoadInfo({ [brand]: SDK } as SdkLoadInfo);
}

const doesSrcInfoHaveVisaSdk = (srcInfo: SRCInfo) => {
  for (let i = 0; i < srcInfo.sdks.length; i++) {
    if (srcInfo.sdks[i].cardBrand === VISA) {
      if (srcInfo.sdks[i].sdkUri.includes(VISA_SDK_URI)) {
        return true;
      }

      return false;
    }
  }

  return false;
};
// END GENAI@CHATGPT4

/**
 * Loads the Mastercard SDK by dynamically creating a script element and appending it to the document head.
 * @returns {Promise<void>} A promise that resolves when the all enabled partner networks sdks are loaded.
 */
export default async function loadPartnerNetworks() {
  const srcInfo: SRCInfo | null = getSrcNetworkInfo();
  const qpNetworks = getSupportedCardNetworks();
  const allNetworks = [] as Promise<void>[];

  if (!srcInfo) {
    throw new Error('No srcInfo found');
  }

  // eslint-disable-next-line
  if (!qpNetworks) {
    throw new Error('No requested networks found');
  }

  // clone srcInfo.sdks array
  const sdks = [...srcInfo.sdks];

  /* START GENAI@GHCOPILOT */
  // default load Visa SDK if merchant config is unset
  if (sdks.length === 0 || !doesSrcInfoHaveVisaSdk(srcInfo)) {
    sdks.unshift({
      apiKey: 'from-communicator',
      cardBrand: VISA,
      domains: ['www.visa.com'],
      panEncryptionKey: 'from-communicator',
      panEncryptionKid: 'from-communicator',
      sdkUri: `${ROOT_DOMAIN}${VISA_SDK_URI}?v2&vsb`,
      sdkUriHash: 'from-communicator'
    });
  }
  /* END GENAI@GHCOPILOT */
  /*eslint-disable-next-line*/
  console.info('ONBOARDED_NETWORKS ::>> ', ONBOARDED_NETWORKS);
  sdks.forEach(sdk => {
    const brand = sdk.cardBrand;
    /* START GENAI@GHCOPILOT */
    isMarkable && performance.mark(`sdk_${brand}_start`);
    /* END GENAI@GHCOPILOT */
    const isValidNetwork = isValidRequestedNetwork(brand, qpNetworks);
    //eslint-disable-next-line
    console.info(`loadPartnerNetworks :: isValidNetwork:>> ${brand}`, isValidNetwork);

    // START GENAI@CHATGPT4
    if (ONBOARDED_NETWORKS.includes(brand)) {
      allNetworks.push(
        new Promise<void>((resolve, reject) => {
          const networkID = `${brand.toLowerCase()}SDK`;
          const partnerScriptTag = document.getElementById(networkID);

          if (!partnerScriptTag && isValidNetwork) {
            const scriptElement = document.createElement('script');
            scriptElement.src = sdk.sdkUri;
            scriptElement.id = networkID;
            scriptElement.onerror = reject;
            document.body.appendChild(scriptElement);
            scriptElement.onload = () => {
              //eslint-disable-next-line
              console.log(`${brand} SDK loaded successfully`);
              resolve();
            };
          } else if (partnerScriptTag) {
            throw `${brand} SDK already loaded`;
          } else if (!isValidNetwork) {
            throw `${brand} not valid for this merchant`;
          }
        })
          .then(() => {
            /* START GENAI@GHCOPILOT */
            isMarkable && performance.mark(`sdk_${brand}_end`);
            isMarkable &&
              performance.measure(`sdk_${brand}`, `sdk_${brand}_start`, `sdk_${brand}_end`);
            //eslint-disable-next-line
            console.info(
              `loadPartnerNetworks :: ${brand} :: ${
                performance.getEntriesByName(`sdk_${brand}`)[0].duration
              }ms`
            );
            /* END GENAI@GHCOPILOT */

            initializeSDK(brand);
          })
          .catch(error => {
            //eslint-disable-next-line
            console.error(`Failed loading ${brand} partner SDK: `, error);
          })
      );
    }
    // END GENAI@CHATGPT4
  });

  return Promise.all(allNetworks);
}
